// client/src/components/ReportsTab.jsx
// "דוחות" tab on CampDetailPage. Two halves:
//   - Top: download tiles for the XLSX exports (transactions, leaderboard).
//   - Bottom: activity summary — per-item EARN/SPEND tallies for the
//     "what did we credit/sell this season?" debrief.

import { useFairDaySummary } from '../hooks/useReports.js';
import Spinner from './Spinner.jsx';
import { formatMoney } from '../lib/format.js';
import { ArrowDownTrayIcon } from './Icons.jsx';

export default function ReportsTab({ campId, currency }) {
  const summaryQ = useFairDaySummary(campId);

  return (
    <div className="space-y-6">
      <DownloadCards campId={campId} />
      <SummarySection q={summaryQ} currency={currency} />
    </div>
  );
}

const DOWNLOADS = [
  {
    href: '/api/camps/{campId}/reports/transactions.xlsx',
    title: 'כל העסקאות',
    description: 'גיליון אחד עם שורה לכל פריט במכירה + שורה לכל זיכוי. מתאים לרישום חשבונאי.',
    tone: 'brand',
  },
  {
    href: '/api/camps/{campId}/reports/leaderboard.xlsx',
    title: 'טבלת מובילים',
    description: 'כל התלמידים + יתרה נוכחית, מסודר מהגבוה לנמוך. נוח להדפסה לפני חלוקת הפרסים.',
    tone: 'gold',
  },
];

const TONE = {
  brand: { tile: 'bg-brand-100 text-brand-700', strap: 'bg-brand-gradient' },
  gold:  { tile: 'bg-gold-100  text-gold-700',  strap: 'bg-gold-gradient'  },
};

function DownloadCards({ campId }) {
  return (
    <section>
      <p className="eyebrow">ייצוא</p>
      <h2 className="h-section mt-1 mb-3">הורדות XLSX</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {DOWNLOADS.map((d) => (
          <DownloadCard
            key={d.title}
            title={d.title}
            description={d.description}
            href={d.href.replace('{campId}', campId)}
            tone={d.tone}
          />
        ))}
      </div>
    </section>
  );
}

function DownloadCard({ title, description, href, tone }) {
  const t = TONE[tone];
  return (
    <a
      href={href}
      download
      className="card overflow-hidden block hover:shadow-pop hover:-translate-y-0.5 hover:border-brand-200 transition-all"
    >
      <div className={`h-1.5 ${t.strap}`} />
      <div className="p-4 flex items-start gap-3">
        <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${t.tile}`}>
          <ArrowDownTrayIcon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-bold text-ink-900">{title}</h3>
            <span className="kbd">.xlsx</span>
          </div>
          <p className="text-sm text-ink-600 mt-1">{description}</p>
        </div>
      </div>
    </a>
  );
}

function SummarySection({ q, currency }) {
  if (q.isLoading) return <Spinner label="טוען סיכום..." />;
  if (q.isError) {
    return (
      <div className="card-danger p-4 text-joy-900 text-sm">
        שגיאה: {q.error.message}
      </div>
    );
  }
  const { earn, spend, totals } = q.data;
  return (
    <div className="space-y-4">
      <div>
        <p className="eyebrow">סיכום פעילות</p>
        <h2 className="h-section mt-1">מה קרה השבוע</h2>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="זיכויים"
          value={totals.earnEvents.toLocaleString('he-IL')}
          hint={`סה״כ ${formatMoney(totals.earnAmount, currency)}`}
          strap="strap-earn"
        />
        <Kpi
          label="חיובים"
          value={totals.spendUnits.toLocaleString('he-IL')}
          hint={`סה״כ ${formatMoney(totals.spendAmount, currency)}`}
          strap="strap-spend"
        />
        <Kpi
          label="סוגי זיכוי שונים"
          value={earn.length.toLocaleString('he-IL')}
        />
        <Kpi
          label="פריטים שונים בחנות"
          value={spend.length.toLocaleString('he-IL')}
        />
      </div>

      <SummaryTable
        title="פריטי זיכוי"
        badge="EARN"
        badgeClass="badge-mint"
        strap="strap-earn"
        rows={earn}
        cols={[
          { key: 'itemLabel', label: 'פריט', flex: true },
          { key: 'count', label: 'פעמים', align: 'end' },
          { key: 'totalAmount', label: 'סה״כ', align: 'end', format: (n) => formatMoney(n, currency) },
        ]}
        emptyText="אין עדיין זיכויים"
      />
      <SummaryTable
        title="פריטי חנות"
        badge="SPEND"
        badgeClass="badge-sky"
        strap="strap-spend"
        rows={spend}
        cols={[
          { key: 'itemLabel', label: 'פריט', flex: true },
          { key: 'units', label: 'יחידות', align: 'end' },
          { key: 'totalAmount', label: 'סה״כ', align: 'end', format: (n) => formatMoney(n, currency) },
        ]}
        emptyText="אין עדיין חיובים"
      />
    </div>
  );
}

function Kpi({ label, value, hint, strap }) {
  return (
    <div className="kpi relative overflow-hidden">
      {strap && <div className={`absolute inset-x-0 top-0 ${strap}`} />}
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="kpi-hint num">{hint}</div>}
    </div>
  );
}

function SummaryTable({ title, badge, badgeClass, strap, rows, cols, emptyText }) {
  return (
    <div className="card overflow-hidden">
      {strap && <div className={strap} />}
      <div className="px-5 py-3 border-b border-ink-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="h-section">{title}</h3>
          {badge && <span className={badgeClass}>{badge}</span>}
        </div>
        <span className="badge-slate num">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-ink-500">{emptyText}</div>
      ) : (
        <ul className="divide-y divide-ink-100">
          {rows.map((r, idx) => (
            <li
              key={`${r.itemLabel}-${idx}`}
              className="px-5 py-2.5 grid grid-cols-[1fr_auto_auto] gap-4 items-center text-sm hover:bg-ink-50/60 transition-colors"
            >
              {cols.map((c) => (
                <span
                  key={c.key}
                  className={`${c.align === 'end' ? 'text-end num font-semibold' : ''} ${c.flex ? 'text-ink-900 truncate' : 'text-ink-700'}`}
                >
                  {c.format ? c.format(r[c.key]) : r[c.key]}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
